import { useState, useEffect, useCallback, useRef } from 'react';

interface TradingStatus {
  isOpen: boolean;
  nextOpenTime: string | null;
  nextCloseTime: string | null;
  countdown: string;
  countdownSeconds: number;
  openTime: string;
  closeTime: string;
  timezone: string;
  weekendEnabled: boolean;
  enabled: boolean;
  manualPaused: boolean;
  pauseReason: string | null;
  pausedCategories: string[];
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

/**
 * 带指数退避重试的 fetch
 */
async function fetchWithRetry(url: string, options: Record<string, unknown>, retries = 3): Promise<Response> {
  let lastErr: unknown;
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url, options as unknown);
      if (res.ok) return res;
      // 5xx 才重试，4xx 不重试
      if (res.status < 500) return res;
      lastErr = new Error(`HTTP ${res.status}`);
    } catch (e) {
      lastErr = e;
    }
    if (i < retries - 1) {
      await new Promise((r) => setTimeout(r, 1000 * 2 ** i));
    }
  }
  throw lastErr;
}

export function useTradingStatus() {
  const [status, setStatus] = useState<TradingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetchingRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    // 防止并发重复请求
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await fetchWithRetry(
        `${SUPABASE_URL}/functions/v1/trading-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
        },
        3,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as { data?: TradingStatus };
      if (json.data) {
        setStatus(json.data);
        setError(false);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // 本地倒计时更新（每秒减1秒）
  useEffect(() => {
    if (!status) return;

    const interval = setInterval(() => {
      setStatus((prev) => {
        if (!prev) return prev;
        const nextSeconds = Math.max(0, prev.countdownSeconds - 1);
        const hours = Math.floor(nextSeconds / 3600);
        const minutes = Math.floor((nextSeconds % 3600) / 60);
        const seconds = nextSeconds % 60;
        return {
          ...prev,
          countdownSeconds: nextSeconds,
          countdown: `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`,
        };
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.countdownSeconds]);

  // 每30秒重新拉一次服务器状态
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  return { status, loading, error, refresh: fetchStatus };
}